/* V209 — Per-agent marketing landing page.
 * URL: /plugin/agent/<slug>
 * Public. Pulls metadata from /api/public/agent/<slug>/meta.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AgentLandingPage() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [locale, setLocale] = useState("sk");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    const browserLocale = (navigator.language || "sk").slice(0, 2).toLowerCase();
    setLocale(browserLocale);
  }, []);

  useEffect(() => {
    setLoading(true); setErr("");
    axios.get(`${API}/public/agent/${slug}/meta?locale=${locale}`)
      .then((r) => setMeta(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "Agent neexistuje." : "Načítavanie zlyhalo."))
      .finally(() => setLoading(false));
  }, [slug, locale]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(""), 1800);
    });
  };

  if (loading) return <div className="min-h-screen bg-zinc-950 text-zinc-300 flex items-center justify-center">Načítavam…</div>;
  if (err || !meta) return <div className="min-h-screen bg-zinc-950 text-rose-400 flex items-center justify-center">{err}</div>;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid={`agent-landing-${slug}`}>
      <Helmet>
        <title>{`${meta.name} · AI Agent · TopRankPulse`}</title>
        <meta name="description" content={meta.desc || meta.benefits.join(" · ")} />
      </Helmet>

      {/* Hero */}
      <section className="border-b border-zinc-800 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/agent-os" className="text-xs text-purple-400 hover:underline">← Späť na marketplace</Link>
          <div className="flex items-start gap-4 mt-4 flex-wrap">
            <div className="text-6xl">{meta.emoji}</div>
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-1 ${meta.is_live ? "bg-emerald-500 text-black" : "bg-amber-500 text-black"}`}>
                  {meta.is_live ? "✓ LIVE" : "⏳ Čaká"}
                </span>
                {meta.price_eur_month && (
                  <span className="text-purple-300 font-black text-lg">€{meta.price_eur_month}/mes</span>
                )}
              </div>
              <h1 className="text-4xl sm:text-5xl font-black leading-tight" data-testid="agent-name">{meta.name}</h1>
              <p className="text-zinc-400 mt-3 text-base sm:text-lg">{meta.desc}</p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {meta.is_live ? (
              <>
                <Link to="/agent-compat" data-testid="cta-scan"
                      className="bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider px-6 py-3 text-sm">
                  🔍 Otestuj kompatibilitu (zadarmo)
                </Link>
                <Link to="/dashboard?tab=api-keys" data-testid="cta-install"
                      className="border border-purple-500 text-purple-300 hover:bg-purple-500/10 font-black uppercase tracking-wider px-6 py-3 text-sm">
                  🚀 Začni 14-day trial
                </Link>
              </>
            ) : (
              <Link to={`/agent/${slug}`} data-testid="cta-vote"
                    className="bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wider px-6 py-3 text-sm">
                ⭐ Hlasuj a získaj 50% off lifetime
              </Link>
            )}
          </div>
          {meta.vote_count > 0 && (
            <p className="text-xs text-zinc-500 mt-3">
              {meta.vote_count} ľudí už hlasovalo / je na waitliste
            </p>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 px-6 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-black mb-6">✨ Čo agent robí</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {(meta.benefits || []).map((b, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 p-5" data-testid={`benefit-${i}`}>
                <div className="text-3xl text-purple-400 mb-2">{i === 0 ? "⚡" : i === 1 ? "💰" : "🌍"}</div>
                <div className="text-sm text-zinc-100">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 px-6 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-black mb-6">🔧 Ako to funguje</h2>
          <div className="grid sm:grid-cols-4 gap-3">
            {(meta.how_it_works || []).map((s) => (
              <div key={s.step} className="bg-zinc-900 border border-zinc-800 p-5" data-testid={`step-${s.step}`}>
                <div className="text-purple-400 font-black text-3xl mb-2">{s.step}</div>
                <div className="font-bold text-sm mb-1">{s.title}</div>
                <div className="text-xs text-zinc-400">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Install methods */}
      <section className="py-12 px-6 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-black mb-6">📦 Install methods (3 spôsoby)</h2>

          {/* JS snippet */}
          <div className="bg-zinc-900 border border-zinc-800 p-5 mb-4" data-testid="install-js">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-black text-lg">A) 1-line JavaScript snippet</h3>
              <span className="text-xs text-zinc-500">~2 min · ľubovoľná stránka</span>
            </div>
            <pre className="bg-black text-amber-300 font-mono text-xs p-4 overflow-x-auto whitespace-pre-wrap">{meta.snippet_template}</pre>
            <button onClick={() => copy(meta.snippet_template, "snippet")}
                    data-testid="copy-snippet-btn"
                    className="mt-3 bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider px-4 py-2 text-xs">
              {copied === "snippet" ? "✓ skopírované" : "📋 Kopírovať"}
            </button>
          </div>

          {/* WP shortcode */}
          <div className="bg-zinc-900 border border-zinc-800 p-5 mb-4" data-testid="install-wp">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-black text-lg">B) WordPress plugin (.zip)</h3>
              <span className="text-xs text-zinc-500">~3 min · WordPress 5.6+</span>
            </div>
            <p className="text-sm text-zinc-400 mb-3">
              Stiahni plugin, nahraj do WP Admin → Plugins → Upload, aktivuj, vlož API key. Použiteľný cez floating bubble alebo shortcode:
            </p>
            <pre className="bg-black text-amber-300 font-mono text-xs p-4 mb-3">{meta.wp_shortcode}</pre>
            <a href={`${API}/plugin/wordpress/toprankpulse-agents.zip`}
               data-testid="wp-download-btn"
               className="inline-block bg-blue-500 hover:bg-blue-400 text-white font-black uppercase tracking-wider px-4 py-2 text-xs">
              ⬇️ Stiahnuť .zip
            </a>
          </div>

          {/* REST API */}
          <div className="bg-zinc-900 border border-zinc-800 p-5" data-testid="install-api">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-black text-lg">C) REST API (developers)</h3>
              <span className="text-xs text-zinc-500">~10 min · Zapier/Make/custom</span>
            </div>
            <pre className="bg-black text-amber-300 font-mono text-xs p-4 overflow-x-auto whitespace-pre-wrap">
{`curl -X POST "https://toprankpulse.com/api/public/agent/${slug}/run" \\
  -H "Authorization: Bearer pk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"message":"Hello","locale":"${meta.locale}"}'`}
            </pre>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-purple-500/10 to-purple-700/5">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black mb-3">Pripravený nasadiť {meta.name}?</h2>
          <p className="text-zinc-300 mb-6">14-day free trial · žiadny credit card · zruš kedykoľvek</p>
          <div className="flex gap-2 justify-center flex-wrap">
            <Link to="/plugin/funnel" data-testid="cta-funnel"
                  className="bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider px-6 py-3 text-sm">
              🚀 Spustiť trial
            </Link>
            <Link to="/pricing" data-testid="cta-pricing-bottom"
                  className="border border-purple-500 text-purple-300 hover:bg-purple-500/10 font-black uppercase tracking-wider px-6 py-3 text-sm">
              💰 Cenník
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800 text-center text-xs text-zinc-500">
        <p>Powered by <span className="text-purple-400 font-bold">TopRankPulse Agent OS</span> · 15 agentov · 6 jazykov</p>
        <p className="mt-2">
          <a href={`${API}/plugin/agent/${slug}/guide.pdf?lang=${meta.locale}`}
             target="_blank" rel="noopener noreferrer"
             data-testid="agent-guide-pdf-link"
             className="text-purple-400 hover:underline font-bold">
            📄 Stiahnuť osobnú príručku k {meta.name} (PDF)
          </a>
        </p>
        <p className="mt-1">
          <Link to="/plugin/docs/sk.pdf" className="text-purple-400 hover:underline">📖 General install docs (PDF, 6 jazykov)</Link>
          {" · "}
          <Link to="/agent-compat" className="text-purple-400 hover:underline">🔍 Compatibility check</Link>
        </p>
      </footer>
    </div>
  );
}
